import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import { useAuthSignalState } from "@/contexts/auth-context";

export interface CoreAccessGroupInfo {
  creatorPublicKey: string;
  accessGroupKeyName: string;
  subscriberPublicKey: string;
}
export type AddAccessGroupInfo = CoreAccessGroupInfo & {
  decryptionSeedHex: string;
};
interface GatedContentContextType {
  subscriptions: Map<string, string>;
  getDecryptionSeedHex: (
    accessGroup: CoreAccessGroupInfo,
  ) => string | undefined;
  addDecryptionSeedHexes: (accessGroups: AddAccessGroupInfo[]) => void;
}

export const GatedContentContext = createContext<GatedContentContextType>({
  subscriptions: new Map(),
  addDecryptionSeedHexes: () => {},
  getDecryptionSeedHex: () => undefined,
});
export const gatedContentSubscriptionsSignal = signal<Map<string, string>>(
  new Map(),
);

const buildSubscriptionKey = (
  creatorPublicKey: string,
  accessGroupKeyName: string,
  subscriberPublicKey: string,
) => `${creatorPublicKey}:${accessGroupKeyName}:${subscriberPublicKey}`;

export const GatedContentProvider = ({ children }: { children: ReactNode }) => {
  const { currentUserPublicKey } = useAuthSignalState();
  const [subscriptions, setSubscriptions] = useState(new Map<string, string>());

  useLayoutEffect(() => {
    gatedContentSubscriptionsSignal.value = subscriptions;
  }, [subscriptions]);

  useLayoutEffect(() => {
    const nextSubscriptions = new Map<string, string>();
    gatedContentSubscriptionsSignal.value = nextSubscriptions;
    setSubscriptions(nextSubscriptions);
  }, [currentUserPublicKey]);

  useEffect(() => {
    return () => {
      gatedContentSubscriptionsSignal.value = new Map();
    };
  }, []);

  const addDecryptionSeedHexes = useCallback(
    (
      accessGroups: {
        creatorPublicKey: string;
        accessGroupKeyName: string;
        subscriberPublicKey: string;
        decryptionSeedHex: string;
      }[],
    ) => {
      setSubscriptions((prevSubscriptions) => {
        const newSubscriptions = new Map(prevSubscriptions);
        accessGroups.forEach((accessGroup) => {
          newSubscriptions.set(
            buildSubscriptionKey(
              accessGroup.creatorPublicKey,
              accessGroup.accessGroupKeyName,
              accessGroup.subscriberPublicKey,
            ),
            accessGroup.decryptionSeedHex,
          );
        });
        return newSubscriptions;
      });
    },
    [],
  );

  const getDecryptionSeedHex = useCallback(
    (accessGroup: CoreAccessGroupInfo) => {
      return gatedContentSubscriptionsSignal.value.get(
        buildSubscriptionKey(
          accessGroup.creatorPublicKey,
          accessGroup.accessGroupKeyName,
          accessGroup.subscriberPublicKey,
        ),
      );
    },
    [],
  );

  return (
    <GatedContentContext.Provider
      value={{
        subscriptions,
        addDecryptionSeedHexes,
        getDecryptionSeedHex,
      }}
    >
      {children}
    </GatedContentContext.Provider>
  );
};

export const useGatedContent = () => {
  useSignals();
  const context = useContext(GatedContentContext);
  if (!context) {
    throw new Error(
      "useSubscriptions must be used within a SubscriptionProvider",
    );
  }

  return {
    ...context,
    subscriptions: gatedContentSubscriptionsSignal.value,
  };
};
